/* eslint-disable curly, quotes */
/* LGC Bulk Product — dynamic pricing and UI interactions */
(function () {
  "use strict";

  /**
   * Format an øre (1/100 DKK) value into a Danish formatted string.
   * e.g. 116500 → "1.165", 4000 → "40"
   */
  function formatPrice(ore) {
    const kr = Math.round(ore) / 100;
    return kr.toLocaleString("da-DK", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  /**
   * Format an insurance value as a full whole-number currency label.
   * e.g. 72000 → "72.000 KR", 500 → "500 KR", 0 → "0 KR"
   */
  function formatInsuranceLabel(value, currencySuffix) {
    const safeValue = Number.isNaN(parseInt(value, 10)) ? 0 : parseInt(value, 10);
    return (
      safeValue.toLocaleString("da-DK", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) +
      " " +
      (currencySuffix || "KR")
    );
  }

  /**
   * Move the slider tooltip to sit above the current thumb position.
   */
  function positionTooltip(slider, tooltip) {
    if (!slider || !tooltip) return;
    const min = parseInt(slider.min, 10);
    const max = parseInt(slider.max, 10);
    const val = parseInt(slider.value, 10);
    const ratio = max === min ? 0 : (val - min) / (max - min);
    // Compensate for thumb radius so tooltip centres correctly at edges
    const thumbRadius = 10; // px, matches CSS width/2
    tooltip.style.left = `calc(${ratio * 100}% - ${thumbRadius * (ratio * 2 - 1)}px)`;
  }

  /**
   * Update the CSS custom property that drives the slider's filled track colour.
   */
  function updateSliderTrack(slider) {
    if (!slider) return;
    const min = parseInt(slider.min, 10);
    const max = parseInt(slider.max, 10);
    const val = parseInt(slider.value, 10);
    const pct = max === min ? 0 : ((val - min) / (max - min)) * 100;
    slider.style.setProperty("--slider-progress", pct + "%");
  }

  /**
   * Get discount percentage from sessionStorage tier data,
   * falling back to the section's data-discount-rate attribute.
   * Discount is stored as a string like "10%" or "10", returns 0 if not found.
   */
  function getDiscountPercentage(wrapper) {
    try {
      const tierData = sessionStorage.getItem("lgcSelectedPricingTier");
      if (tierData) {
        const tier = JSON.parse(tierData);
        if (tier.discount) {
          const discountStr = String(tier.discount).trim();
          const discountNum = parseFloat(discountStr.replace("%", ""));
          if (!isNaN(discountNum) && discountNum > 0) return discountNum;
        }
      }
    } catch (_) {}
    // Fallback to section setting
    const sectionDiscount = parseFloat(wrapper.dataset.discountRate || 0);
    return isNaN(sectionDiscount) ? 0 : sectionDiscount;
  }

  /**
   * Resolve handling fee info from selected pricing tier context for this product URL.
   */
  function getHandlingFeeConfigForCurrentProduct() {
    try {
      const tierData = sessionStorage.getItem("lgcSelectedPricingTier");
      if (!tierData) return null;

      const tier = JSON.parse(tierData);
      const payloadPath = tier.targetUrl ? new URL(tier.targetUrl, window.location.origin).pathname : "";
      const currentPath = window.location.pathname;

      if (payloadPath && payloadPath !== currentPath) return null;

      const variantId = parseInt(tier.handlingFeeVariantId || "", 10);
      const unitPrice = parseInt(tier.handlingFeePrice || "", 10);

      if (Number.isNaN(variantId) || variantId <= 0) return null;

      return {
        variantId,
        unitPrice: Number.isNaN(unitPrice) ? 0 : unitPrice,
        title: tier.handlingFeeTitle || "",
      };
    } catch (_) {
      return null;
    }
  }

  function getSelectedPricingTierForCurrentProduct() {
    try {
      const tierData = sessionStorage.getItem("lgcSelectedPricingTier");
      if (!tierData) return null;

      const tier = JSON.parse(tierData);
      const payloadPath = tier.targetUrl ? new URL(tier.targetUrl, window.location.origin).pathname : "";
      const currentPath = window.location.pathname;

      if (payloadPath && payloadPath !== currentPath) return null;

      return tier;
    } catch (_) {
      return null;
    }
  }

  /**
   * Resolve selected pricing tier card price (in øre) for the current product URL.
   */
  function getTierCardPriceForCurrentProduct() {
    try {
      const tierData = sessionStorage.getItem("lgcSelectedPricingTier");
      if (!tierData) return null;

      const tier = JSON.parse(tierData);
      const payloadPath = tier.targetUrl ? new URL(tier.targetUrl, window.location.origin).pathname : "";
      const currentPath = window.location.pathname;

      if (payloadPath && payloadPath !== currentPath) return null;

      const rawPrice = String(tier.price || "").trim();
      if (!rawPrice) return null;

      // Supports values like "129", "129 KR", "129,5", "129.50"
      const normalized = rawPrice
        .replace(/kr/gi, "")
        .replace(/\s/g, "")
        .replace(",", ".")
        .replace(/[^\d.]/g, "");
      const dkk = parseFloat(normalized);
      if (Number.isNaN(dkk) || dkk <= 0) return null;

      return Math.round(dkk * 100);
    } catch (_) {
      return null;
    }
  }

  /**
   * Initialise one instance of the bulk product widget.
   */
  function initBulkProduct(wrapper) {
    if (!wrapper || wrapper.dataset.lgcBulkProductInitialized === "true") {
      return;
    }

    wrapper.dataset.lgcBulkProductInitialized = "true";

    // ── Config from data attributes ──
    const paddingTopMobile = parseInt(wrapper.dataset.paddingTopMobile || 0, 10);
    const paddingBottomMobile = parseInt(wrapper.dataset.paddingBottomMobile || 0, 10);
    const paddingTopDesktop = parseInt(wrapper.dataset.paddingTopDesktop || 0, 10);
    const paddingBottomDesktop = parseInt(wrapper.dataset.paddingBottomDesktop || 0, 10);
    const layoutMaxWidth = parseInt(wrapper.dataset.layoutMaxWidth || 0, 10);
    const mediaColumnWidth = parseInt(wrapper.dataset.mediaColumnWidth || 52, 10);
    const mediaColumnWidthTablet = parseInt(wrapper.dataset.mediaColumnWidthTablet || 48, 10);
    const mediaMaxWidth = parseInt(wrapper.dataset.mediaMaxWidth || 500, 10);
    const contentPaddingDesktop = parseInt(wrapper.dataset.contentPaddingDesktop || 56, 10);
    const contentPaddingMobile = parseInt(wrapper.dataset.contentPaddingMobile || 30, 10);
    const fallbackPricePerCard = parseInt(wrapper.dataset.pricePerCard || "0", 10); // already in øre (Shopify variant.price)
    const semiRigidsPrice = parseInt(wrapper.dataset.semiRigidsPrice || 0, 10); // already in øre
    const insuranceRate = parseFloat(wrapper.dataset.insuranceRate || 0); // %
    const minCards = parseInt(wrapper.dataset.minCards || 1, 10);
    // 100 = show tax-inclusive prices, 100 + VAT rate = strip VAT (EU/UK reverse charge customers)
    const vatDivisor = parseInt(wrapper.dataset.vatDivisor || "100", 10) || 100;
    // Translated currency label, rendered server-side from locales
    const currencySuffix = wrapper.dataset.currencySuffix || "KR";
    const singleGradingLockLabel = wrapper.dataset.singleGradingLockLabel || "Kun 1 grading ad gangen";
    const singleGradingAlert =
      wrapper.dataset.singleGradingAlert || "Du kan kun have en grading service i kurven ad gangen.";

    /** Convert a tax-inclusive øre amount to what the customer is actually charged. */
    function exVat(ore) {
      return vatDivisor === 100 ? ore : (ore * 100) / vatDivisor;
    }

    // Extract numeric value from max declared value (e.g., "3200 KR" → 3200)
    function parseMaxDeclaredValue(str) {
      if (!str) return 0;
      const digits = String(str).replace(/[^\d]/g, "");
      const parsed = parseInt(digits, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    const maxDeclaredValue = parseMaxDeclaredValue(wrapper.dataset.maxDeclaredValue);

    wrapper.style.setProperty("--lgc-section-padding-top-mobile", paddingTopMobile + "px");
    wrapper.style.setProperty("--lgc-section-padding-bottom-mobile", paddingBottomMobile + "px");
    wrapper.style.setProperty("--lgc-section-padding-top-desktop", paddingTopDesktop + "px");
    wrapper.style.setProperty("--lgc-section-padding-bottom-desktop", paddingBottomDesktop + "px");
    if (layoutMaxWidth > 0) wrapper.style.setProperty("--lgc-layout-max-width", layoutMaxWidth + "px");
    wrapper.style.setProperty("--lgc-media-column-width", mediaColumnWidth + "%");
    wrapper.style.setProperty("--lgc-media-column-width-tablet", mediaColumnWidthTablet + "%");
    wrapper.style.setProperty("--lgc-media-max-width", mediaMaxWidth + "px");
    wrapper.style.setProperty("--lgc-content-padding-desktop", contentPaddingDesktop + "px");
    wrapper.style.setProperty("--lgc-content-padding-mobile", contentPaddingMobile + "px");

    // ── DOM references ──
    const cardInput = wrapper.querySelector(".lgc-addon--cards .lgc-qty-input");
    const cardPropertyInput = wrapper.querySelector(".lgc-card-count-property");
    const cardsPriceEl = wrapper.querySelector('[id^="lgc-cards-price"]');

    const insuranceSlider = wrapper.querySelector(".lgc-range-slider");
    const insuranceTooltip = wrapper.querySelector(".lgc-slider-tooltip");
    const insuranceManualInput = wrapper.querySelector(".lgc-insurance-manual__input");
    const insuranceDisplay = wrapper.querySelector('[id^="lgc-insurance-display"]');
    const insurancePriceEl = wrapper.querySelector('[id^="lgc-insurance-price"]');

    const semiRadios = wrapper.querySelectorAll(".lgc-semi-rigids-radio");
    const semiQtyRow = wrapper.querySelector(".lgc-semi-rigids-qty");
    const semiCountInput = wrapper.querySelector(".lgc-semi-rigids-count");
    const semiPriceEl = wrapper.querySelector('[id^="lgc-semi-price"]');

    let totalPriceEl = wrapper.querySelector(".lgc-total-price");
    const form = wrapper.querySelector(".lgc-order-form");
    const submitButton = form?.querySelector('[type="submit"]');
    const submitButtonDefaultLabel = submitButton?.textContent || "";
    const submitButtonDefaultHtml = submitButton?.innerHTML || "";
    const insuranceAddOnVariantInput = wrapper.querySelector(".lgc-insurance-add-on-variant-id");
    const insuranceAddOnVariantsEl = wrapper.querySelector(".lgc-insurance-add-on-variants");
    const insuranceStepInput = wrapper.querySelector(".lgc-insurance-step");
    const semiRigidsAddOnVariantInput = wrapper.querySelector(".lgc-semi-rigids-add-on-variant-id");

    let isSubmitting = false;

    const productHandle = wrapper.dataset.productHandle || "";
    const storageKey = productHandle ? "lgc-bulk:" + productHandle : null;
    const stateTtlMs = 5 * 60 * 1000;

    // ── Persist / restore form state ──
    function saveState() {
      if (!storageKey) return;
      try {
        const checkedSemiRadio = wrapper.querySelector(".lgc-semi-rigids-radio:checked");
        const state = {
          cards: cardInput?.value,
          insurance: insuranceSlider?.value,
          semiSelected: checkedSemiRadio?.value,
          semiCount: semiCountInput?.value,
          savedAt: Date.now(),
        };
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch (_) {}
    }

    function setInitialInsuranceFromMaxDeclared() {
      if (!insuranceSlider || maxDeclaredValue <= 0) return;

      // Default to nearest 1000 down from max declared value.
      // If max is below 1000, keep the max value itself.
      const roundedToNearestThousand = Math.floor(maxDeclaredValue / 1000) * 1000;
      const initialValue = roundedToNearestThousand > 0 ? roundedToNearestThousand : maxDeclaredValue;
      insuranceSlider.value = String(Math.max(0, initialValue));
      updateSliderTrack(insuranceSlider);
      positionTooltip(insuranceSlider, insuranceTooltip);
      syncManualInsuranceInput();
      if (insuranceTooltip) {
        insuranceTooltip.textContent = formatInsuranceLabel(parseInt(insuranceSlider.value, 10), currencySuffix);
      }
    }

    function restoreState() {
      if (!storageKey) {
        setInitialInsuranceFromMaxDeclared();
        return;
      }
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) {
          setInitialInsuranceFromMaxDeclared();
          saveState();
          return;
        }
        const state = JSON.parse(raw);

        const savedAt = parseInt(state?.savedAt || "0", 10);
        const isExpired = !savedAt || Date.now() - savedAt > stateTtlMs;
        if (isExpired) {
          localStorage.removeItem(storageKey);
          setInitialInsuranceFromMaxDeclared();
          saveState();
          return;
        }

        if (state.cards !== undefined && cardInput) {
          const min = parseInt(cardInput.min || minCards, 10);
          const max = cardInput.max ? parseInt(cardInput.max, 10) : Infinity;
          const val = Math.min(max, Math.max(min, parseInt(state.cards, 10) || min));
          cardInput.value = String(val);
        }

        const parsedInsurance = parseInt(state.insurance, 10);
        const shouldRestoreInsurance = state.insurance !== undefined && !Number.isNaN(parsedInsurance);
        const shouldReinitializeInsurance = maxDeclaredValue > 0 && (!shouldRestoreInsurance || parsedInsurance <= 0);

        if (shouldReinitializeInsurance) {
          setInitialInsuranceFromMaxDeclared();
          saveState();
        } else if (insuranceSlider) {
          let restoredValue = clampInsuranceValue(parsedInsurance);
          // Cap to max declared value (rounded down to nearest 1000)
          if (maxDeclaredValue > 0 && restoredValue > maxDeclaredValue) {
            restoredValue = Math.floor(maxDeclaredValue / 1000) * 1000;
          }
          insuranceSlider.value = String(restoredValue);
          updateSliderTrack(insuranceSlider);
          positionTooltip(insuranceSlider, insuranceTooltip);
          syncManualInsuranceInput();
          if (insuranceTooltip) {
            insuranceTooltip.textContent = formatInsuranceLabel(parseInt(insuranceSlider.value, 10), currencySuffix);
          }
        }

        if (state.semiSelected !== undefined) {
          semiRadios.forEach(function (radio) {
            radio.checked = radio.value === state.semiSelected;
          });
          if (semiQtyRow) semiQtyRow.classList.toggle("is-visible", state.semiSelected === "yes");
        }

        if (state.semiCount !== undefined && semiCountInput) {
          const min = parseInt(semiCountInput.min || 1, 10);
          const max = getSemiMax();
          const val = Math.min(max, Math.max(min, parseInt(state.semiCount, 10) || min));
          semiCountInput.value = String(val);
        }
      } catch (_) {
        setInitialInsuranceFromMaxDeclared();
      }
    }

    // ── Helpers ──
    function getCardCount() {
      return Math.max(minCards, parseInt(cardInput?.value || minCards, 10));
    }

    function getInsuranceValue() {
      if (!insuranceSlider) return 0;
      const rawValue = parseInt(insuranceSlider.value, 10);
      return capInsuranceToMaxDeclared(Number.isNaN(rawValue) ? 0 : rawValue);
    }

    function getAddOnVariantId(input) {
      const parsed = parseInt(input?.value || "", 10);
      return Number.isNaN(parsed) ? null : parsed;
    }

    function parseAmountValue(value) {
      const digits = String(value || "").replace(/[^\d]/g, "");
      if (!digits) return null;
      const parsed = parseInt(digits, 10);
      return Number.isNaN(parsed) ? null : parsed;
    }

    function getInsuranceAddOnVariants() {
      if (!insuranceAddOnVariantsEl) return [];

      try {
        const parsed = JSON.parse(insuranceAddOnVariantsEl.textContent || "[]");
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error(error);
        return [];
      }
    }

    function getInsuranceVariantAmount(variant) {
      if (!variant) return null;

      const candidateValues = [variant.title, variant.option1, variant.option2, variant.option3, variant.sku]
        .map(parseAmountValue)
        .filter(function (value) {
          return value !== null && value > 0;
        });

      return candidateValues.length ? candidateValues[0] : null;
    }

    function getInsuranceStep() {
      const parsed = parseInt(insuranceStepInput?.value || "", 10);
      return Number.isNaN(parsed) || parsed <= 0 ? 1000 : parsed;
    }

    function resolveInsuranceAddOn(insuranceValue) {
      const variants = getInsuranceAddOnVariants();
      if (!variants.length || insuranceValue <= 0) return null;

      const activeVariant =
        variants.find(function (v) {
          return v && v.id && v.available !== false;
        }) || variants[0];

      if (!activeVariant?.id) return null;

      const step = getInsuranceStep();
      const quantity = Math.max(1, Math.round(insuranceValue / step));

      return { id: activeVariant.id, quantity: quantity };
    }

    function syncInsuranceVariant() {
      if (!insuranceAddOnVariantInput) return null;

      const insuranceValue = getInsuranceValue();
      const insuranceConfig = resolveInsuranceAddOn(insuranceValue);
      insuranceAddOnVariantInput.value = insuranceConfig?.id ? String(insuranceConfig.id) : "";
      return insuranceConfig;
    }

    function syncCardProperty() {
      if (!cardInput || !cardPropertyInput) return;
      cardPropertyInput.value = String(getCardCount());
    }

    function clampInsuranceValue(rawValue) {
      if (!insuranceSlider) return 0;

      const min = parseInt(insuranceSlider.min || 0, 10);
      const max = parseInt(insuranceSlider.max || 0, 10);
      const step = parseInt(insuranceSlider.step || 1, 10);
      const parsed = parseInt(rawValue, 10);
      const safeValue = Number.isNaN(parsed) ? min : parsed;
      const clamped = Math.min(max, Math.max(min, safeValue));

      if (step <= 1) return clamped;
      return Math.round(clamped / step) * step;
    }

    function capInsuranceToMaxDeclared(insuranceValue) {
      // If max declared value is not set or is 0, return the value as-is
      if (maxDeclaredValue <= 0) return insuranceValue;

      // If insurance value exceeds max declared value, cap it
      if (insuranceValue > maxDeclaredValue) {
        const step = getInsuranceStep();
        // Round down to the nearest step that doesn't exceed maxDeclaredValue
        const cappedValue = Math.floor(maxDeclaredValue / step) * step;
        return Math.max(0, cappedValue);
      }

      return insuranceValue;
    }

    function syncManualInsuranceInput() {
      if (!insuranceManualInput || !insuranceSlider) return;
      insuranceManualInput.value = insuranceSlider.value;
    }

    function getSemiRigidsSelected() {
      const checked = wrapper.querySelector(".lgc-semi-rigids-radio:checked");
      return checked?.value === "yes";
    }

    // Inventory cap set on the HTML input at page render (or Infinity if not tracked)
    const semiInventoryCap = (function () {
      if (!semiCountInput?.max) return Infinity;
      const m = parseInt(semiCountInput.max, 10);
      return Number.isNaN(m) || m < 0 ? Infinity : m;
    })();

    function getSemiMax() {
      const cardCount = getCardCount();
      return Math.min(cardCount, semiInventoryCap);
    }

    function updateSemiMax() {
      if (!semiCountInput) return;
      const newMax = getSemiMax();
      if (newMax === Infinity) {
        semiCountInput.removeAttribute("max");
      } else {
        semiCountInput.max = String(newMax);
      }
      // Clamp current value down if it now exceeds the new max
      const current = parseInt(semiCountInput.value, 10);
      if (!Number.isNaN(current) && current > newMax) {
        semiCountInput.value = String(newMax);
      }
      // Always fire so stepper button states (disabled) are refreshed
      semiCountInput.dispatchEvent(new Event("input"));
    }

    function getSemiCount() {
      const max = getSemiMax();
      return semiCountInput ? Math.min(max, Math.max(1, parseInt(semiCountInput.value || 1, 10))) : 0;
    }

    function buildMainItemProperties() {
      const properties = {
        "Antal kort": String(getCardCount()),
      };
      const selectedTier = getSelectedPricingTierForCurrentProduct();

      const insuranceValue = getInsuranceValue();
      const cappedInsuranceValue = capInsuranceToMaxDeclared(insuranceValue);
      const semiSelected = getSemiRigidsSelected();
      const insuranceAddOnConfig = syncInsuranceVariant();
      const semiRigidsAddOnVariantId = getAddOnVariantId(semiRigidsAddOnVariantInput);

      if (cappedInsuranceValue > 0 && !insuranceAddOnConfig?.id) {
        properties["Forsikring"] = String(cappedInsuranceValue);
      }

      if (!semiRigidsAddOnVariantId) {
        properties["Semi Rigids"] = semiSelected ? "yes" : "no";
      }

      // Store max declared value and insurance step for use in cart updates
      if (maxDeclaredValue > 0) {
        properties["_lgc_max_declared_value"] = String(maxDeclaredValue);
      }
      const insuranceStep = getInsuranceStep();
      if (insuranceStep > 0) {
        properties["_lgc_insurance_step"] = String(insuranceStep);
      }

      if (selectedTier?.title) {
        properties["_lgc_pricing_tier"] = String(selectedTier.title);
      }
      if (selectedTier?.targetUrl) {
        properties["_lgc_pricing_target_url"] = String(selectedTier.targetUrl);
      }

      const activeDiscount = getDiscountPercentage(wrapper);
      if (activeDiscount > 0) {
        properties["_lgc_discount_rate"] = String(activeDiscount);
      }

      return properties;
    }

    function getMainItemPayloadFromForm() {
      const payload = {
        id: parseInt(form?.querySelector('[name="id"]')?.value || "", 10),
        quantity: getCardCount(),
        properties: {},
      };

      if (!form) return payload;

      const formData = new FormData(form);
      formData.forEach(function (value, name) {
        if (typeof value !== "string") return;

        if (name === "id") {
          const parsedId = parseInt(value, 10);
          if (!Number.isNaN(parsedId)) payload.id = parsedId;
          return;
        }

        if (name === "quantity") {
          const parsedQuantity = parseInt(value, 10);
          if (!Number.isNaN(parsedQuantity) && parsedQuantity > 0) payload.quantity = parsedQuantity;
          return;
        }

        if (name === "selling_plan") {
          if (value) payload.selling_plan = value;
          return;
        }

        const propertyMatch = name.match(/^properties\[(.+)\]$/);
        if (propertyMatch) {
          payload.properties[propertyMatch[1]] = value;
        }
      });

      return payload;
    }

    async function getCartItems() {
      const cartDataUrl = `${window.routes?.cart_url || "/cart"}.js`;

      try {
        const response = await fetch(cartDataUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) return [];

        const cart = await response.json();
        return Array.isArray(cart?.items) ? cart.items : [];
      } catch (_) {
        // If cart lookup fails, do not block checkout flow unexpectedly.
        return [];
      }
    }

    async function getGradingMainCartState(mainVariantId) {
      const items = await getCartItems();

      const gradingMainItems = items.filter(function (item) {
        return item?.properties?._lgc_item_type === "main";
      });

      const variantId = parseInt(mainVariantId, 10);
      const existingMainForVariant = gradingMainItems.find(function (item) {
        return parseInt(item?.id, 10) === variantId;
      });

      const hasConflictingMain = gradingMainItems.some(function (item) {
        return parseInt(item?.id, 10) !== variantId;
      });

      return {
        existingMainForVariant: existingMainForVariant || null,
        hasConflictingMain,
      };
    }

    function getExistingMainProperties(existingMainItem, fallbackBundleId, currentProperties) {
      const existingProps =
        existingMainItem?.properties && typeof existingMainItem.properties === "object"
          ? { ...existingMainItem.properties }
          : null;

      if (existingProps) {
        Object.assign(existingProps, currentProperties || {});
        existingProps._lgc_bundle_id = existingProps._lgc_bundle_id || fallbackBundleId;
        existingProps._lgc_item_type = "main";
        return existingProps;
      }

      return {
        ...(currentProperties || buildMainItemProperties()),
        _lgc_bundle_id: fallbackBundleId,
        _lgc_item_type: "main",
      };
    }

    function setSubmitButtonLocked(isLocked) {
      if (!submitButton) return;

      if (isLocked) {
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-disabled", "true");
        submitButton.textContent = singleGradingLockLabel;
      } else if (!isSubmitting) {
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-disabled");
        submitButton.innerHTML = submitButtonDefaultHtml || submitButtonDefaultLabel;
        totalPriceEl = submitButton.querySelector(".lgc-total-price") || wrapper.querySelector(".lgc-total-price");
      }
    }

    async function syncSubmitButtonLock() {
      const mainVariantId = parseInt(form?.querySelector('[name="id"]')?.value || "", 10);
      if (Number.isNaN(mainVariantId)) {
        setSubmitButtonLocked(false);
        recalculate();
        return;
      }

      const cartState = await getGradingMainCartState(mainVariantId);
      setSubmitButtonLocked(cartState.hasConflictingMain);
      if (!cartState.hasConflictingMain) recalculate();
    }

    async function handleBulkSubmit(event) {
      if (!form) return;

      event.preventDefault();

      // Let other app listeners (for example wholesale plugins) process
      // submit-time form mutations before we snapshot form data.
      await Promise.resolve();

      if (isSubmitting || !submitButton) return;

      const mainVariantId = parseInt(form.querySelector('[name="id"]')?.value || "", 10);
      if (Number.isNaN(mainVariantId)) {
        form.submit();
        return;
      }

      const baseMainItem = getMainItemPayloadFromForm();
      const cardCount = getCardCount();
      const insuranceValue = getInsuranceValue();
      const semiSelected = getSemiRigidsSelected();
      const semiCount = getSemiCount();
      const insuranceAddOnConfig = syncInsuranceVariant();
      const semiRigidsAddOnVariantId = getAddOnVariantId(semiRigidsAddOnVariantInput);
      const cartAddUrl = `${window.routes?.cart_add_url || "/cart/add"}.js`;
      const cartUrl = window.routes?.cart_url || "/cart";
      const handlingFeeConfig = getHandlingFeeConfigForCurrentProduct();
      const cartState = await getGradingMainCartState(mainVariantId);

      if (cartState.hasConflictingMain) {
        alert(singleGradingAlert);
        window.location.href = cartUrl;
        return;
      }

      const existingBundleId = cartState.existingMainForVariant?.properties?._lgc_bundle_id || "";
      const bundleId = existingBundleId || `lgc-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
      const mainItemProperties = getExistingMainProperties(cartState.existingMainForVariant, bundleId, {
        ...baseMainItem.properties,
        ...buildMainItemProperties(),
      });

      const items = [
        {
          id: baseMainItem.id,
          quantity: cardCount,
          properties: mainItemProperties,
          ...(baseMainItem.selling_plan ? { selling_plan: baseMainItem.selling_plan } : {}),
        },
      ];

      if (handlingFeeConfig?.variantId) {
        items.push({
          id: handlingFeeConfig.variantId,
          quantity: cardCount,
          properties: {
            _lgc_bundle_id: bundleId,
            _lgc_item_type: "handling_fee",
            _lgc_handling_fee: "true",
            _lgc_locked: "true",
          },
        });
      }

      if (insuranceAddOnConfig?.id && insuranceValue > 0) {
        items.push({
          id: insuranceAddOnConfig.id,
          quantity: insuranceAddOnConfig.quantity || 1,
          properties: {
            Forsikring: String(insuranceValue),
            _lgc_item_type: "insurance_addon",
          },
        });
      }

      if (semiRigidsAddOnVariantId && semiSelected) {
        items.push({
          id: semiRigidsAddOnVariantId,
          quantity: semiCount,
          properties: {
            _lgc_bundle_id: bundleId,
            _lgc_item_type: "semi_rigids_addon",
          },
        });
      }

      isSubmitting = true;
      submitButton.setAttribute("disabled", "disabled");
      submitButton.setAttribute("aria-disabled", "true");

      try {
        const response = await fetch(cartAddUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ items: items }),
        });

        const payload = await response.json();
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || "Unable to add items to cart.");
        }

        window.location.href = cartUrl;
      } catch (error) {
        console.error(error);
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-disabled");
        isSubmitting = false;
      }
    }

    // ── Recalculate all prices ──
    function recalculate() {
      const cardCount = getCardCount();
      const insuranceValue = getInsuranceValue();
      const semiSelected = getSemiRigidsSelected();
      const semiCount = getSemiCount();

      syncCardProperty();

      if (insuranceTooltip) {
        insuranceTooltip.textContent = formatInsuranceLabel(insuranceValue, currencySuffix);
      }

      // Cards
      const tierCardPrice = getTierCardPriceForCurrentProduct();
      const cardUnitPrice = tierCardPrice || (Number.isNaN(fallbackPricePerCard) ? 0 : fallbackPricePerCard);
      const cardsTotal = cardCount * cardUnitPrice;
      if (cardsPriceEl) cardsPriceEl.textContent = "+ " + formatPrice(exVat(cardsTotal)) + " " + currencySuffix;

      // Insurance: derive cost from the add-on variant price × quantity (same as what goes into cart)
      const insuranceConfig = resolveInsuranceAddOn(insuranceValue);
      let insuranceCost = 0; // in øre
      if (insuranceConfig) {
        const variants = getInsuranceAddOnVariants();
        const variant = variants.find(function (v) {
          return v && v.id === insuranceConfig.id;
        });
        if (variant && variant.price) {
          insuranceCost = variant.price * insuranceConfig.quantity;
        }
      }
      // Apply discount
      const discount = getDiscountPercentage(wrapper);
      if (discount > 0) {
        insuranceCost = Math.round(insuranceCost * (1 - discount / 100));
      }

      if (insurancePriceEl) {
        const insuranceDkk = exVat(insuranceCost) / 100;
        insurancePriceEl.textContent =
          "+ " +
          insuranceDkk.toLocaleString("da-DK", { minimumFractionDigits: 0, maximumSignificantDigits: 21 }) +
          " " +
          currencySuffix;
      }
      if (insuranceDisplay) insuranceDisplay.textContent = formatInsuranceLabel(insuranceValue, currencySuffix);

      // Semi rigids
      const semiTotal = semiSelected ? semiCount * semiRigidsPrice : 0;
      if (semiPriceEl) semiPriceEl.textContent = "+ " + formatPrice(exVat(semiTotal)) + " " + currencySuffix;

      // Handling fee from selected pricing tier (price per card)
      const handlingFeeConfig = getHandlingFeeConfigForCurrentProduct();
      const handlingFeeTotal = handlingFeeConfig ? cardCount * (handlingFeeConfig.unitPrice || 0) : 0;

      // Grand total
      const grandTotal = cardsTotal + insuranceCost + semiTotal + handlingFeeTotal;
      if (totalPriceEl) totalPriceEl.textContent = formatPrice(exVat(grandTotal));
    }

    // ── Quantity stepper buttons ──
    wrapper.querySelectorAll(".lgc-qty-stepper").forEach(function (stepper) {
      const input = stepper.querySelector(".lgc-qty-input");
      if (!input) return;
      const minusBtn = stepper.querySelector(".lgc-qty-btn--minus");
      const plusBtn = stepper.querySelector(".lgc-qty-btn--plus");

      function syncButtons() {
        const min = parseInt(input.min || 1, 10);
        const max = input.max !== "" && input.max !== undefined ? parseInt(input.max, 10) : Infinity;
        const val = parseInt(input.value, 10);
        if (minusBtn) minusBtn.disabled = val <= min;
        if (plusBtn) plusBtn.disabled = !Number.isNaN(val) && max !== Infinity && val >= max;
      }

      minusBtn?.addEventListener("click", function () {
        const min = parseInt(input.min || 1, 10);
        const newVal = Math.max(min, parseInt(input.value, 10) - 1);
        if (input.value !== String(newVal)) {
          input.value = newVal;
          input.dispatchEvent(new Event("input"));
        }
        syncButtons();
      });

      plusBtn?.addEventListener("click", function () {
        const max = input.max !== "" && input.max !== undefined ? parseInt(input.max, 10) : Infinity;
        const newVal = Math.min(max, parseInt(input.value, 10) + 1);
        if (input.value !== String(newVal)) {
          input.value = newVal;
          input.dispatchEvent(new Event("input"));
        }
        syncButtons();
      });

      input.addEventListener("input", syncButtons);
      syncButtons(); // set initial disabled state
    });

    // ── Card count ──
    cardInput?.addEventListener("input", function () {
      updateSemiMax();
      recalculate();
      saveState();
      document.dispatchEvent(
        new CustomEvent("lgc:card-count-change", {
          detail: { cardCount: getCardCount() },
        })
      );
    });
    cardInput?.addEventListener("change", function () {
      updateSemiMax();
      recalculate();
      saveState();
      document.dispatchEvent(
        new CustomEvent("lgc:card-count-change", {
          detail: { cardCount: getCardCount() },
        })
      );
    });

    // ── Insurance slider ──
    if (insuranceSlider) {
      insuranceSlider.addEventListener("input", function () {
        // Cap insurance to max declared value (rounded down to nearest 1000)
        let sliderValue = parseInt(insuranceSlider.value, 10);
        if (maxDeclaredValue > 0 && sliderValue > maxDeclaredValue) {
          sliderValue = Math.floor(maxDeclaredValue / 1000) * 1000;
          insuranceSlider.value = String(sliderValue);
        }

        updateSliderTrack(insuranceSlider);
        positionTooltip(insuranceSlider, insuranceTooltip);
        syncManualInsuranceInput();
        recalculate();
        saveState();
      });

      insuranceSlider.addEventListener("change", function () {
        syncManualInsuranceInput();
        saveState();
      });

      // Initialise track and tooltip on page load
      // Apply max declared value capping to initial value
      let initialValue = parseInt(insuranceSlider.value, 10);
      if (maxDeclaredValue > 0 && initialValue > maxDeclaredValue) {
        initialValue = Math.floor(maxDeclaredValue / 1000) * 1000;
        insuranceSlider.value = String(initialValue);
      }

      updateSliderTrack(insuranceSlider);
      positionTooltip(insuranceSlider, insuranceTooltip);
      syncManualInsuranceInput();
      if (insuranceTooltip) {
        insuranceTooltip.textContent = formatInsuranceLabel(parseInt(insuranceSlider.value, 10), currencySuffix);
      }
    }

    if (insuranceManualInput && insuranceSlider) {
      insuranceManualInput.addEventListener("input", function () {
        let normalized = clampInsuranceValue(insuranceManualInput.value);

        // Cap to max declared value (rounded down to nearest 1000)
        if (maxDeclaredValue > 0 && normalized > maxDeclaredValue) {
          normalized = Math.floor(maxDeclaredValue / 1000) * 1000;
        }

        insuranceSlider.value = String(normalized);
        updateSliderTrack(insuranceSlider);
        positionTooltip(insuranceSlider, insuranceTooltip);
        recalculate();
        saveState();
      });

      insuranceManualInput.addEventListener("change", function () {
        let normalized = clampInsuranceValue(insuranceManualInput.value);

        // Cap to max declared value (rounded down to nearest 1000)
        if (maxDeclaredValue > 0 && normalized > maxDeclaredValue) {
          normalized = Math.floor(maxDeclaredValue / 1000) * 1000;
        }

        insuranceManualInput.value = String(normalized);
        saveState();
      });
    }

    // ── Semi rigids toggle ──
    semiRadios.forEach(function (radio) {
      radio.addEventListener("change", function () {
        const selected = getSemiRigidsSelected();
        if (semiQtyRow) semiQtyRow.classList.toggle("is-visible", selected);
        // Auto-sync semi count to current card count (clamped to max inventory)
        if (selected && semiCountInput && cardInput) {
          const max = getSemiMax();
          semiCountInput.value = String(Math.min(max, parseInt(cardInput.value, 10) || 1));
        }
        recalculate();
        saveState();
      });
    });

    // ── Semi count ──
    semiCountInput?.addEventListener("input", function () {
      recalculate();
      saveState();
    });
    semiCountInput?.addEventListener("change", function () {
      const min = parseInt(semiCountInput.min || 1, 10);
      const max = getSemiMax();
      const val = Math.min(max, Math.max(min, parseInt(semiCountInput.value || min, 10) || min));
      if (semiCountInput.value !== String(val)) semiCountInput.value = String(val);
      recalculate();
      saveState();
    });

    // ── Sync semi count when card count changes ──
    cardInput?.addEventListener("input", function () {
      if (getSemiRigidsSelected() && semiCountInput) {
        semiCountInput.value = cardInput.value;
      }
    });

    // ── Initial render ──
    restoreState();
    syncSubmitButtonLock();
    updateSemiMax();
    if (semiQtyRow) semiQtyRow.classList.toggle("is-visible", getSemiRigidsSelected());
    syncCardProperty();
    syncInsuranceVariant();
    recalculate();

    form?.addEventListener("submit", handleBulkSubmit);

    // Listen for pricing tier changes to recalculate insurance with new discount
    wrapper.addEventListener("recalculate-prices", function () {
      recalculate();
    });
  }

  function initBulkProducts(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll(".lgc-bulk-product-wrapper").forEach(initBulkProduct);
  }

  // Bootstrap all instances on the page
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initBulkProducts(document);
    });
  } else {
    initBulkProducts(document);
  }

  document.addEventListener("product-info:loaded", function (event) {
    initBulkProducts(event.target);
  });

  document.addEventListener("product-info:updated", function (event) {
    initBulkProducts(event.target);
  });

  document.addEventListener("shopify:section:load", function (event) {
    initBulkProducts(event.target);
  });

  // Listen for pricing tier changes to recalculate insurance with new discount
  document.addEventListener("lgc:pricing-tier-change", function () {
    // Recalculate insurance prices on all visible bulk product forms
    document.querySelectorAll(".lgc-bulk-product-wrapper").forEach(function (wrapper) {
      const recalcEvent = new CustomEvent("recalculate-prices");
      wrapper.dispatchEvent(recalcEvent);
    });
  });
})();
